import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { useI18n } from "../../i18n";

const Cancellation = () => {
  const { lang, t } = useI18n();
  return (
    <>
      <Header />
      
      <section className="pt-36 pb-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="rise font-display text-5xl md:text-6xl leading-[1.05] mb-4 text-ink">
            {lang === "en" ? "Cancellation Policy" : t("footer.legal.cancellation")}
          </h1>
          {lang !== "en" && (
            <p className="mb-4 rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink-2">{t("legal.notice")}</p>
          )}
          <p className="text-ink-3 text-sm mb-12 pb-8 border-b border-line">
            Last Updated: December 2024
          </p>

          <div className="legal" dir="ltr" lang="en">
            <section>
              <h2>1. Overview</h2>
              <p className="text-base leading-relaxed mb-4">
                At Optivra Tech, we understand that circumstances may change and you may need to cancel your project or service. This Cancellation Policy outlines the terms and conditions under which cancellations are accepted and processed.
              </p>
              <p className="text-base leading-relaxed">
                We encourage open communication. If you're experiencing concerns about your project, please contact us first so we can work together to find a solution.
              </p>
            </section>

            <section>
              <h2>2. Cancellation by Client</h2>
              
              <h3>2.1 Before Project Commencement</h3>
              <p className="text-base leading-relaxed mb-4">
                You may cancel your order before work begins:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li><strong>Timeline:</strong> Within 7 days of payment</li>
                <li><strong>Conditions:</strong> No project work has started, no resources allocated</li>
                <li><strong>Refund:</strong> Full refund of the advance payment</li>
                <li><strong>Process:</strong> Email founder@optivratech.com with "Cancellation Request"</li>
              </ul>

              <h3>2.2 After Project Initiation</h3>
              <p className="text-base leading-relaxed mb-4">
                Once work has begun, cancellation terms depend on project progress:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li><strong>Early Stage (0-25% complete):</strong> 75% refund of remaining amount</li>
                <li><strong>Mid Stage (25-50% complete):</strong> 50% refund of remaining amount</li>
                <li><strong>Advanced Stage (50-75% complete):</strong> 25% refund of remaining amount</li>
                <li><strong>Near Completion (75%+ complete):</strong> No refund</li>
              </ul>
              <p className="text-base leading-relaxed">
                You will be charged for all work completed to date, including design, development, and any third-party services purchased for your project.
              </p>

              <h3>2.3 After Milestone Approval</h3>
              <p className="text-base leading-relaxed mb-4">
                For milestone-based projects:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Once you approve a milestone, payment for that milestone is non-refundable</li>
                <li>You may cancel remaining milestones with 7 days notice</li>
                <li>Refunds apply only to unstarted milestones</li>
                <li>Deliverables for completed milestones will be provided</li>
              </ul>
            </section>

            <section>
              <h2>3. Cancellation by Optivra Tech</h2>
              <p className="text-base leading-relaxed mb-4">
                We reserve the right to cancel a project in the following situations:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Non-payment of agreed fees within specified timelines</li>
                <li>Lack of response or cooperation from client despite multiple reminders</li>
                <li>Project requirements that violate applicable laws or regulations</li>
                <li>Scope changes that fundamentally alter the project agreement</li>
                <li>Technical impossibility or unforeseen limitations</li>
                <li>Force majeure events preventing project completion</li>
              </ul>
              <p className="text-base leading-relaxed mb-4">
                In such cases:
              </p>
              <ul className="space-y-2 ml-4">
                <li>We will provide 7 days written notice</li>
                <li>You will be charged only for work completed</li>
                <li>Remaining amount will be refunded within 14 business days</li>
                <li>All completed deliverables will be provided to you</li>
              </ul>
            </section>

            <section>
              <h2>4. Cancellation Process</h2>
              
              <h3>Step 1: Submit Request</h3>
              <p className="text-base leading-relaxed mb-4">
                Send a cancellation request to founder@optivratech.com with:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Subject line: "Project Cancellation Request"</li>
                <li>Your name and project details</li>
                <li>Order/Invoice number</li>
                <li>Reason for cancellation</li>
                <li>Preferred refund method (if applicable)</li>
              </ul>

              <h3>Step 2: Review & Assessment</h3>
              <p className="text-base leading-relaxed mb-4">
                Our team will:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Acknowledge receipt within 24 hours</li>
                <li>Review project status and work completed</li>
                <li>Calculate applicable charges and refunds</li>
                <li>Respond within 2-3 business days</li>
              </ul>

              <h3>Step 3: Confirmation</h3>
              <p className="text-base leading-relaxed mb-4">
                We will send you:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Cancellation confirmation email</li>
                <li>Breakdown of charges for work completed</li>
                <li>Refund amount (if applicable)</li>
                <li>Timeline for refund processing</li>
              </ul>

              <h3>Step 4: Final Deliverables</h3>
              <p className="text-base leading-relaxed mb-4">
                Upon cancellation:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>All work completed to date will be delivered</li>
                <li>Source files and documentation provided</li>
                <li>Access to staging environments maintained for 7 days</li>
              </ul>

              <h3>Step 5: Refund Processing</h3>
              <p className="text-base leading-relaxed">
                If eligible for refund, it will be processed within 7-14 business days to your original payment method.
              </p>
            </section>

            <section>
              <h2>5. Service-Specific Cancellation Terms</h2>
              
              <h3>5.1 Website Development Projects</h3>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Design phase cancellation: 80% refund</li>
                <li>Development phase cancellation: Based on completion %</li>
                <li>Testing phase cancellation: No refund</li>
              </ul>

              <h3>5.2 AI Automation Solutions</h3>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Discovery phase cancellation: 90% refund</li>
                <li>Development phase cancellation: Based on milestone completion</li>
                <li>Integration phase cancellation: Minimal refund</li>
              </ul>

              <h3>5.3 Consulting Services</h3>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Cancel 48 hours before session: Full refund</li>
                <li>Cancel 24-48 hours before: 50% charge</li>
                <li>Cancel less than 24 hours: No refund</li>
                <li>No-show: Full charge</li>
              </ul>

              <h3>5.4 Monthly Support/Maintenance</h3>
              <ul className="space-y-2 ml-4">
                <li>Require 30 days notice</li>
                <li>Cancellation effective at end of billing period</li>
                <li>Pro-rated refund for unused time</li>
                <li>No refund for current month services</li>
              </ul>
            </section>

            <section>
              <h2>6. Mutual Termination</h2>
              <p className="text-base leading-relaxed mb-4">
                Both parties may agree to terminate the project by mutual consent:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Terms negotiated on case-by-case basis</li>
                <li>Fair compensation for work completed</li>
                <li>All deliverables handed over</li>
                <li>Confidentiality obligations maintained</li>
                <li>Positive professional relationship preserved</li>
              </ul>
            </section>

            <section>
              <h2>7. Non-Cancellable Situations</h2>
              <p className="text-base leading-relaxed mb-4">
                The following cannot be cancelled:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Completed and delivered projects</li>
                <li>Third-party services purchased specifically for your project</li>
                <li>Domain names and hosting purchased on your behalf</li>
                <li>Completed consulting sessions</li>
                <li>Custom licenses and subscriptions</li>
              </ul>
            </section>

            <section>
              <h2>8. Emergency Cancellation</h2>
              <p className="text-base leading-relaxed mb-4">
                In case of genuine emergencies (medical, natural disasters, etc.):
              </p>
              <ul className="space-y-2 ml-4">
                <li>Contact us immediately</li>
                <li>We may pause the project instead of cancelling</li>
                <li>Special consideration given to refund terms</li>
                <li>Documentation of emergency may be required</li>
              </ul>
            </section>

            <section>
              <h2>9. After Cancellation</h2>
              <p className="text-base leading-relaxed mb-4">
                Following cancellation:
              </p>
              <ul className="space-y-2 ml-4">
                <li>All project-related communications cease</li>
                <li>Your data remains confidential as per our privacy policy</li>
                <li>You may engage our services again in the future</li>
                <li>We maintain professional relationship for future opportunities</li>
              </ul>
            </section>

            <section>
              <h2>10. Dispute Resolution</h2>
              <p className="text-base leading-relaxed mb-4">
                If you disagree with cancellation terms:
              </p>
              <ul className="space-y-2 ml-4">
                <li>We encourage open discussion first</li>
                <li>Escalate to senior management if needed</li>
                <li>Consider mediation before legal action</li>
                <li>Governed by laws of India</li>
              </ul>
            </section>

            <section>
              <h2>11. Important Notes</h2>
              <div className="rounded-lg bg-surface border border-line p-6 space-y-4">
                <p className="text-base">
                  <strong>Notice Period:</strong> Always provide written notice via email for cancellation requests.
                </p>
                <p className="text-base">
                  <strong>Communication:</strong> Keep communication professional and document all correspondence.
                </p>
                <p className="text-base">
                  <strong>Alternative Solutions:</strong> Before cancelling, consider project pause, scope reduction, or timeline extension.
                </p>
              </div>
            </section>

            <section>
              <h2>12. Contact for Cancellation</h2>
              <p className="text-base leading-relaxed mb-2">
                To initiate cancellation or discuss concerns:
              </p>
              <div className="rounded-lg bg-surface border border-line p-6 mt-4">
                <p className="text-base mb-2"><strong>Optivra Tech</strong></p>
                <p className="text-base mb-2">GST: 08BNOPS7805R1ZT</p>
                <p className="text-base mb-2">Address: C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</p>
                <p className="text-base mb-2">Email: founder@optivratech.com</p>
                <p className="text-base mb-2">Phone: +91 86198 38718</p>
                <p className="text-base mb-2">Subject: "Project Cancellation Request"</p>
                <p className="text-base">Response Time: Within 24 hours</p>
              </div>
            </section>

            <section>
              <h2>13. Policy Updates</h2>
              <p className="text-base leading-relaxed">
                This Cancellation Policy may be updated periodically. Changes will be effective immediately upon posting on our website. It is your responsibility to review this policy regularly. Continued use of our services after changes indicates acceptance of the modified policy.
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Cancellation;
